import { useState, useEffect } from "react";
import { ArrowRight, MapPin, Calendar } from "lucide-react";

const TARGET_DATE = new Date("2026-06-27T09:00:00").getTime();

const Hero = () => {
  const [timeLeft, setTimeLeft] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0 });
  const [bgIndex, setBgIndex] = useState(0);

  const images = [
    "/images/hero-ai.png",
    "/images/hero-community.png",
    "/images/hero-conference.png"
  ];

  useEffect(() => {
    const timer = setInterval(() => {
      const now = Date.now();
      const diff = TARGET_DATE - now;
      if (diff <= 0) {
        clearInterval(timer);
        return;
      }
      setTimeLeft({
        days: Math.floor(diff / (1000 * 60 * 60 * 24)),
        hours: Math.floor((diff / (1000 * 60 * 60)) % 24),
        minutes: Math.floor((diff / (1000 * 60)) % 60),
        seconds: Math.floor((diff / 1000) % 60),
      });
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    const bgTimer = setInterval(() => {
      setBgIndex((prev) => (prev + 1) % images.length);
    }, 6000);
    return () => clearInterval(bgTimer);
  }, [images.length]);

  const pad = (n) => String(n).padStart(2, "0");

  const handleScroll = (id) => {
    const el = document.querySelector(id);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section
      data-testid="hero-section"
      className="relative min-h-screen flex items-center justify-center overflow-hidden"
    >
      {/* Background Slideshow */}
      <div className="absolute inset-0">
        {images.map((src, index) => (
          <img
            key={src}
            src={src}
            alt=""
            className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-1000 ${
              index === bgIndex ? "opacity-25" : "opacity-0"
            }`}
          />
        ))}
        <div className="absolute inset-0 bg-gradient-to-b from-[#09090B] via-[#09090B]/80 to-[#09090B]" />
      </div>

      {/* Glows */}
      <div className="hero-glow hero-glow-teal" />
      <div className="hero-glow hero-glow-purple" />
      <div className="hero-glow hero-glow-cyan" />

      {/* Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 text-center pt-24 pb-16">
        {/* Tag */}
        <div className="animate-fade-in-up inline-flex items-center gap-2 px-4 py-2 bg-zinc-900/80 border border-zinc-800 mb-8">
          <span className="w-2 h-2 rounded-full bg-teal-500 animate-pulse" />
          <span className="text-xs uppercase tracking-[0.2em] text-zinc-400 font-medium">
            Oke-Ogun 2026
          </span>
        </div>

        {/* Headline */}
        <h1
          className="animate-fade-in-up animation-delay-200 text-5xl sm:text-6xl lg:text-7xl font-extrabold tracking-tighter leading-[0.9] mb-6"
          data-testid="hero-title"
        >
          <span className="text-white">ICT Summit</span>
          <br />
          <span className="gradient-text">In the Age of AI</span>
        </h1>

        {/* Subtitle */}
        <p className="animate-fade-in-up animation-delay-400 text-zinc-400 text-base sm:text-lg max-w-2xl mx-auto mb-8 leading-relaxed">
          A forward-looking technology gathering preparing individuals, businesses, and institutions
          for life and work in the Age of Artificial Intelligence.
        </p>

        {/* Event Info */}
        <div className="animate-fade-in-up animation-delay-400 flex flex-wrap items-center justify-center gap-6 text-zinc-400 text-sm mb-12">
          <div className="flex items-center gap-2">
            <Calendar size={16} className="text-teal-500" />
            <span>June 27, 2026</span>
          </div>
          <div className="flex items-center gap-2">
            <MapPin size={16} className="text-teal-500" />
            <span>Oredegbe Digital Hall, 5, Koleoso Street, Koomi, Saki, Oyo State</span>
          </div>
        </div>

        {/* Countdown */}
        <div
          className="animate-fade-in-up animation-delay-600 flex items-center justify-center gap-3 sm:gap-6 mb-12"
          data-testid="countdown-timer"
        >
          {[
            { val: timeLeft.days, label: "Days" },
            { val: timeLeft.hours, label: "Hours" },
            { val: timeLeft.minutes, label: "Mins" },
            { val: timeLeft.seconds, label: "Secs" },
          ].map((item, i) => (
            <div key={item.label} className="flex flex-col items-center">
              <div className="bg-zinc-900 border border-zinc-800 w-16 h-16 sm:w-20 sm:h-20 flex items-center justify-center">
                <span className="countdown-digit text-2xl sm:text-3xl font-bold text-white">
                  {pad(item.val)}
                </span>
              </div>
              <span className="text-[10px] sm:text-xs uppercase tracking-[0.15em] text-zinc-500 mt-2 font-medium">
                {item.label}
              </span>
            </div>
          ))}
        </div>

        {/* CTAs */}
        <div className="animate-fade-in-up animation-delay-800 flex flex-col sm:flex-row items-center justify-center gap-4">
          <a
            href="https://hayeni.com/events/oke-ogun-ict-summit-2026-1"
            target="_blank"
            rel="noopener noreferrer"
            className="pulse-cta bg-teal-600 hover:bg-teal-500 text-white px-8 py-4 font-semibold tracking-wide transition-all flex items-center gap-2 w-full sm:w-auto justify-center"
            data-testid="hero-register-btn"
          >
            Register for Free
            <ArrowRight size={18} />
          </a>
          <button
            onClick={() => handleScroll("#about")}
            className="bg-transparent border border-zinc-700 hover:border-zinc-500 text-zinc-100 px-8 py-4 font-medium tracking-wide transition-all w-full sm:w-auto"
            data-testid="hero-learn-more-btn"
          >
            Learn More
          </button>
        </div>

        {/* 1000 Tickets Badge */}
        <div className="animate-fade-in-up animation-delay-800 mt-8 inline-flex items-center gap-2 text-sm text-zinc-500">
          <span className="text-teal-400 font-bold">1,000</span>
          <span>free tickets available</span>
        </div>
      </div>
    </section>
  );
};

export default Hero;
